import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Eye } from "lucide-react";

const STATUSES = ["new", "contacted", "qualified", "approved", "rejected"];
const TONE = { new: "bg-finblue/10 text-finblue", contacted: "bg-gold/15 text-[#8a6d2f]", qualified: "bg-navy/10 text-navy", approved: "bg-success/10 text-success", rejected: "bg-danger/10 text-danger" };

export default function ApplicationsAdmin() {
  const qc = useQueryClient();
  const { data: apps = [] } = useQuery({
    queryKey: ["admin-apps"],
    queryFn: async () => (await api.get("/admin/partner-applications")).data,
    refetchInterval: 20000,
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
  });
  const [view, setView] = useState(null);
  const [notes, setNotes] = useState("");

  const update = useMutation({
    mutationFn: async ({ id, body }) => api.patch(`/admin/partner-applications/${id}`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-apps"] }); },
  });

  return (
    <div>
      <h1 className="font-heading text-2xl font-bold text-ink">Partner Applications</h1>
      <p className="mt-1 text-sm text-subtle">Review and manage IB / affiliate applications.</p>

      <div className="mt-6 overflow-hidden rounded-xl border border-hair bg-white shadow-sm">
        <Table>
          <TableHeader><TableRow className="bg-canvas"><TableHead>Name</TableHead><TableHead>Business</TableHead><TableHead>Email</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {apps.length === 0 && <TableRow><TableCell colSpan={5} className="py-10 text-center text-subtle">No applications yet.</TableCell></TableRow>}
            {apps.map((a) => (
              <TableRow key={a.id} data-testid={`app-row-${a.id}`}>
                <TableCell className="font-medium text-ink">{a.full_name}</TableCell>
                <TableCell className="text-sm text-subtle">{a.business_type || "—"}</TableCell>
                <TableCell className="text-sm text-subtle">{a.email}</TableCell>
                <TableCell>
                  <Select value={a.status} onValueChange={(v) => update.mutate({ id: a.id, body: { status: v } })}>
                    <SelectTrigger className="h-8 w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
                <TableCell><Button size="icon" variant="ghost" onClick={() => { setView(a); setNotes(a.notes || ""); }}><Eye className="h-4 w-4" /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!view} onOpenChange={() => setView(null)}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader><DialogTitle>Application — {view?.full_name}</DialogTitle></DialogHeader>
          {view && (
            <div className="space-y-1.5 text-sm">
              {[["Email", view.email], ["Phone", view.phone], ["Country", view.country], ["Company", view.company_name], ["Website", view.website], ["Social", view.social_media], ["Business Type", view.business_type], ["Primary Markets", view.primary_markets], ["Monthly Leads", view.estimated_monthly_leads], ["Broker Relationships", view.existing_broker_relationships], ["Audience Size", view.audience_size]].map(([k, v]) => (
                <p key={k}><span className="text-subtle">{k}:</span> {v || "—"}</p>
              ))}
              {view.additional_information && <p className="rounded-lg bg-canvas p-3">{view.additional_information}</p>}
              <div className="pt-3">
                <label className="text-sm font-medium text-ink">Internal Notes</label>
                <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" data-testid="app-notes" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => { update.mutate({ id: view.id, body: { notes } }); toast.success("Notes saved"); setView(null); }} className="bg-finblue hover:bg-finblueDark" data-testid="app-save-notes">Save Notes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
